<template>
  <div v-if="show" class="fa-overlay">
    <!-- Topbar -->
    <div class="fa-topbar">
      <div class="fa-topbar-controls">
        <div class="fa-ctrl">
          <label>Escolher hotel</label>
          <select v-model="hotelId" class="form-control" @change="onHotelChange">
            <option :value="null" disabled>Selecione o hotel</option>
            <option v-for="h in hotels" :key="h.id" :value="h.id">{{ h.name }}</option>
          </select>
        </div>
        <div class="fa-ctrl" v-if="hotelId">
          <label>Período da alocação</label>
          <select v-model="dateRangeId" class="form-control" @change="loadRooms">
            <option :value="null" disabled>Selecione o período</option>
            <option v-for="r in dateRanges" :key="r.id" :value="r.id">
              {{ $moment(r.check_in).format('DD/MM') }} – {{ $moment(r.check_out).format('DD/MM/YYYY') }}
            </option>
          </select>
        </div>
        <div class="fa-ctrl fa-ctrl-cat" v-if="hotelId">
          <label>Categoria</label>
          <multiselect
            v-model="selectedCategories"
            :options="categories"
            :multiple="true"
            label="name"
            track-by="id"
            placeholder="Todas"
            tag-placeholder="Selecione uma ou mais categorias"
            select-label=""
            deselect-label="Remover"
            selected-label="Selecionada"
            :close-on-select="false"
            :limit="1"
            :limit-text="rcLimitText"
            @input="onCategoryChange"
          ></multiselect>
        </div>
        <div class="fa-ctrl" v-if="hotelId">
          <label>Acomodação</label>
          <select v-model="typeRoom" class="form-control" @change="loadRooms">
            <option :value="null">Todas</option>
            <option v-for="(t, i) in typeAccommodations" :key="i" :value="t.type_room">{{ t.desc_room }}</option>
          </select>
        </div>
      </div>

      <button class="fa-close" @click="close" title="Fechar"><i class="bx bx-x"></i></button>
    </div>

    <!-- Body -->
    <div class="fa-body">
      <!-- Empty state -->
      <div v-if="!hotelId" class="fa-empty">
        <i class="bx bxs-hotel"></i>
        <p>Selecione um hotel para começar a alocar.</p>
      </div>

      <template v-else>
        <!-- LEFT: rooms -->
        <div class="fa-rooms">
          <div class="fa-rooms-head">
            <div class="fa-legend">
              <span><i class="bx bxs-circle text-success"></i> Disponível</span>
              <span><i class="bx bxs-circle text-warning"></i> Parcial</span>
              <span><i class="bx bxs-circle text-danger"></i> Lotado</span>
            </div>
          </div>

          <div class="fa-room-scroll">
            <div v-if="!loadingRooms && !rooms.length" class="fa-muted p-2">Nenhum quarto encontrado para este filtro.</div>

            <div v-else class="fa-room-grid">
              <div
                v-for="room in rooms"
                :key="room.id"
                class="fa-room"
                :class="[roomStatus(room), { selected: selectedRoom && selectedRoom.id === room.id }]"
                @click="selectRoom(room)"
              >
                <i v-if="roomStatus(room) === 'empty'" class="bx bxs-check-circle fa-room-badge fa-badge-check"></i>
                <span v-else-if="roomStatus(room) === 'partial'" class="fa-room-badge fa-badge-partial">{{ room.allocated_count }}</span>
                <span v-else class="fa-room-badge fa-badge-full">{{ room.allocated_count }}</span>
                <div class="fa-room-num">{{ room.id }}</div>
                <div class="fa-room-cap">{{ room.allocated_count }}/{{ room.capacity }}</div>
                <div class="fa-room-type">{{ room.type_room_label || room.type_room }}</div>
              </div>
            </div>
          </div>

          <!-- Loading overlay (padrão form-builder) -->
          <transition name="fa-fade">
            <div v-if="loadingRooms" class="fa-loading-overlay">
              <div class="fa-loading-content">
                <div class="fa-loading-spinner"></div>
                <p>Carregando quartos...</p>
              </div>
            </div>
          </transition>

          <!-- Footer fixo: detalhe do quarto selecionado -->
          <div v-if="selectedRoom" class="fa-room-detail">
            <div class="fa-room-detail-head">
              <strong>Quarto {{ selectedRoom.id }}</strong>
              <span class="fa-slots">{{ remainingSlots(selectedRoom) }} vaga(s) livre(s)</span>
            </div>
            <div class="fa-occupants">
              <span v-if="!occupants(selectedRoom).length" class="fa-muted">Sem ocupantes.</span>
              <div v-for="alloc in occupants(selectedRoom)" :key="alloc.id" class="fa-occupant">
                <span class="fa-occupant-name" @click="showParticipant(alloc.participant_id)" title="Ver ficha do participante">
                  {{ alloc.participant && alloc.participant.data ? alloc.participant.data.name : '—' }}
                </span>
                <span class="fa-remove" @click="removeAllocation(alloc)" title="Remover">×</span>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT: participants -->
        <div class="fa-participants">
          <div class="fa-part-head">
            <div class="fa-part-head-top">
              <span class="fa-count">
                <i class="bx bx-user"></i>
                {{ participants.length }} participante(s)
              </span>
            </div>
            <div class="fa-part-head-bottom">
              <div class="search-box flex-grow-1">
                <div class="position-relative">
                  <input
                    type="text"
                    class="form-control"
                    v-model="participantSearch"
                    placeholder="Buscar participante"
                  />
                  <i class="bx bx-search-alt search-icon"></i>
                </div>
              </div>
              <button class="btn" :class="hasActiveFilters ? 'btn-warning' : 'btn-light'" @click="openFilters">
                <i class="mdi mdi-filter mr-1"></i> Filtrar
                <span v-if="hasActiveFilters" class="badge badge-light ml-1">{{ activeFilterCount }}</span>
              </button>
              <b-dropdown
                right
                no-caret
                variant="light"
                :toggle-class="companionFilter !== '' ? 'fa-comp-btn fa-comp-btn-active' : 'fa-comp-btn'"
                :title="companionFilterLabel"
              >
                <template #button-content>
                  <i class="bx bx-group"></i>
                </template>
                <b-dropdown-item :active="companionFilter === ''" @click="setCompanionFilter('')">
                  <i v-if="companionFilter === ''" class="bx bx-check mr-1"></i> Todos
                </b-dropdown-item>
                <b-dropdown-item :active="companionFilter === '1'" @click="setCompanionFilter('1')">
                  <i v-if="companionFilter === '1'" class="bx bx-check mr-1"></i> Com acompanhante
                </b-dropdown-item>
                <b-dropdown-item :active="companionFilter === '0'" @click="setCompanionFilter('0')">
                  <i v-if="companionFilter === '0'" class="bx bx-check mr-1"></i> Sem acompanhante
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>

          <div class="fa-part-list">
            <div v-if="loadingParticipants" class="fa-muted p-3">Carregando...</div>
            <div v-else-if="!participants.length" class="fa-muted p-3">Nenhum participante livre com esses filtros.</div>

            <label
              v-for="p in participants"
              :key="p.id"
              class="fa-part-item"
              :class="{ disabled: isDisabled(p) }"
            >
              <input
                type="checkbox"
                :value="p.id"
                v-model="selectedParticipantIds"
                :disabled="isDisabled(p)"
              />
              <div class="fa-part-info">
                <div class="fa-part-name">{{ p.name }}</div>
                <div class="fa-part-meta">
                  <span v-if="p.gender">{{ genderLabel(p.gender) }}</span>
                  <span v-if="groupName(p)">· {{ groupName(p) }}</span>
                  <span v-if="p.has_companion > 0" class="fa-comp-badge">
                    <i class="bx bx-group"></i> {{ p.has_companion }} acomp.
                  </span>
                </div>
              </div>
            </label>
          </div>

          <div v-if="selectedRoom && !dateRangeId" class="fa-alloc-hint">
            <i class="bx bx-info-circle"></i>
            <span v-if="dateRanges.length">Selecione o <strong>período</strong> (range de datas) acima para habilitar a alocação.</span>
            <span v-else>Este hotel não tem período cadastrado — cadastre um período antes de alocar.</span>
          </div>

          <div class="fa-part-footer">
            <span class="fa-selected-count">{{ selectedParticipantIds.length }} selecionado(s)</span>
            <button
              class="btn btn-primary fa-alloc-btn"
              :disabled="!canAllocate"
              :title="selectedRoom && !dateRangeId ? 'Selecione o período (range de datas) para habilitar' : ''"
              @click="allocate"
            >
              <i class="bx bx-plus mr-1"></i>
              Alocar<span v-if="selectedRoom"> no quarto {{ selectedRoom.id }}</span>
            </button>
          </div>
        </div>
      </template>
    </div>

    <!-- Filtros dinâmicos (reusa o mesmo componente do /participants) -->
    <dynamic-filter-modal ref="modalFilter" context="participants" @filtered="applyFilters" />

    <!-- Ficha do participante (mesmo modal do /participants) -->
    <modal-participant v-model="formParticipant" ref="formParticipant" />
  </div>
</template>

<script>
import Swal from 'sweetalert2'
import Multiselect from 'vue-multiselect'
import DynamicFilterModal from '@/components/modals/filters/DynamicFilter'
import ModalParticipant from '@/pages/participants/Form'
import { buildApiParams } from '@/modules/utils/filters'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import { listHotels } from '../hotel/services'
import { list as listCategories } from '../hotel/categories/services'
import { typesAccommodations } from '../hotel/accommodation/services'
import { list as listRooms } from '../rooms/services'
import { postGroupData, delAllocation } from '../hotel/allocation/services'
import { list as listParticipants } from '@/pages/participants/services'

export default {
  name: 'FreeAllocation',
  components: { Multiselect, DynamicFilterModal, ModalParticipant },
  data () {
    return {
      show: false,
      hotels: [],
      hotelId: null,
      dateRanges: [],
      dateRangeId: null,
      categories: [],
      selectedCategories: [],
      typeAccommodations: [],
      typeRoom: null,
      rooms: [],
      loadingRooms: false,
      selectedRoom: null,
      participants: [],
      loadingParticipants: false,
      participantSearch: '',
      searchTimer: null,
      activeFilters: {},
      companionFilter: '',
      selectedParticipantIds: [],
      formParticipant: false
    }
  },
  computed: {
    // O filtro de acompanhante é desacoplado — não conta como "Filtro" avançado
    hasActiveFilters () {
      return Object.keys(this.activeFilters).length > 0
    },
    activeFilterCount () {
      return Object.keys(this.activeFilters).length
    },
    companionFilterLabel () {
      if (this.companionFilter === '1') return 'Filtrando: com acompanhante'
      if (this.companionFilter === '0') return 'Filtrando: sem acompanhante'
      return 'Filtrar por acompanhante'
    },
    canAllocate () {
      return this.selectedRoom &&
        this.dateRangeId &&
        this.selectedParticipantIds.length > 0 &&
        this.remainingSlots(this.selectedRoom) > 0
    },
    selectedCategoryIds () {
      return this.selectedCategories.map(c => c.id)
    }
  },
  watch: {
    // Busca em tempo real (debounce) — sem precisar apertar Enter
    participantSearch () {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        if (this.hotelId) this.loadParticipants()
      }, 350)
    }
  },
  methods: {
    open () {
      this.show = true
      this.resetState()
      this.loadHotels()
    },
    close () {
      this.show = false
      this.resetState()
    },
    resetState () {
      this.hotelId = null
      this.dateRanges = []
      this.dateRangeId = null
      this.categories = []
      this.selectedCategories = []
      this.typeAccommodations = []
      this.typeRoom = null
      this.rooms = []
      this.selectedRoom = null
      this.participants = []
      this.participantSearch = ''
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.activeFilters = {}
      this.companionFilter = ''
      this.selectedParticipantIds = []
    },
    unwrap (res) {
      const d = res && res.data ? res.data : {}
      return d.data || d || []
    },
    async loadHotels () {
      try {
        const res = await listHotels({ limit: 1000 })
        this.hotels = this.unwrap(res)
        // Auto-seleciona quando só há um hotel cadastrado
        if (this.hotels.length === 1) {
          this.hotelId = this.hotels[0].id
          this.onHotelChange()
        }
      } catch (e) {
        errorMessage('Falha ao carregar hotéis.')
      }
    },
    onHotelChange () {
      const hotel = this.hotels.find(h => h.id === this.hotelId)
      this.dateRanges = (hotel && hotel.date_ranges) ? hotel.date_ranges : []
      this.dateRangeId = this.dateRanges.length === 1 ? this.dateRanges[0].id : null
      this.selectedCategories = []
      this.typeRoom = null
      this.typeAccommodations = []
      this.selectedRoom = null
      this.selectedParticipantIds = []
      this.loadCategories()
      this.loadRooms()
      this.loadParticipants()
    },
    async loadCategories () {
      if (!this.hotelId) return
      try {
        const res = await listCategories({ hotel_id: this.hotelId, limit: 100 })
        this.categories = this.unwrap(res)
      } catch (e) {
        this.categories = []
      }
    },
    rcLimitText (count) {
      return `+${count}`
    },
    onCategoryChange () {
      this.typeRoom = null
      this.loadTypeAccommodations()
      this.loadRooms()
    },
    async loadTypeAccommodations () {
      if (!this.hotelId || !this.selectedCategoryIds.length) {
        this.typeAccommodations = []
        return
      }
      try {
        const res = await typesAccommodations({ hotel_id: this.hotelId, category_id: this.selectedCategoryIds })
        // Categorias diferentes podem repetir o mesmo tipo — dedupe para o dropdown
        const seen = {}
        this.typeAccommodations = this.unwrap(res).filter(t => {
          if (seen[t.type_room]) return false
          seen[t.type_room] = true
          return true
        })
      } catch (e) {
        this.typeAccommodations = []
      }
    },
    async loadRooms () {
      if (!this.hotelId) return
      this.loadingRooms = true
      const params = {
        hotel_id: this.hotelId,
        all: 1,
        include: 'allocation.participant'
      }
      if (this.dateRangeId) params.hotel_date_range_id = this.dateRangeId
      if (this.selectedCategoryIds.length) params.category_id = this.selectedCategoryIds
      if (this.typeRoom) params.type_room = this.typeRoom
      try {
        const res = await listRooms(params)
        this.rooms = this.unwrap(res)
        if (this.selectedRoom) {
          this.selectedRoom = this.rooms.find(r => r.id === this.selectedRoom.id) || null
        }
      } catch (e) {
        this.rooms = []
        errorMessage('Falha ao carregar quartos.')
      } finally {
        this.loadingRooms = false
      }
    },
    async loadParticipants () {
      if (!this.hotelId) return
      this.loadingParticipants = true
      const base = {
        not_allocated: 1,
        include: 'group,companion',
        search: this.participantSearch || null,
        limit: 500
      }
      if (this.companionFilter !== '') base.has_companion = this.companionFilter
      const params = buildApiParams(base, this.activeFilters)
      // Regra fixa desta tela: só participantes CONFIRMADOS (status 3)
      params.status = 3
      try {
        const res = await listParticipants(params)
        this.participants = this.unwrap(res)
      } catch (e) {
        this.participants = []
        errorMessage('Falha ao carregar participantes.')
      } finally {
        this.loadingParticipants = false
      }
    },
    applyFilters (filters) {
      this.activeFilters = filters || {}
      this.loadParticipants()
    },
    setCompanionFilter (val) {
      this.companionFilter = val
      this.loadParticipants()
    },
    showParticipant (participantId) {
      if (participantId && this.$refs.formParticipant) {
        this.$refs.formParticipant.open(participantId)
        this.formParticipant = true
      }
    },
    openFilters () {
      this.$refs.modalFilter.open()
    },
    roomStatus (room) {
      const allocated = room.allocated_count || 0
      if (allocated >= room.capacity) return 'full'
      if (allocated > 0) return 'partial'
      return 'empty'
    },
    remainingSlots (room) {
      return Math.max(0, (room.capacity || 0) - (room.allocated_count || 0))
    },
    occupants (room) {
      return (room && room.allocation && room.allocation.data) ? room.allocation.data : []
    },
    selectRoom (room) {
      this.selectedRoom = room
      this.selectedParticipantIds = []
    },
    isSelected (p) {
      return this.selectedParticipantIds.includes(p.id)
    },
    isDisabled (p) {
      if (!this.selectedRoom) return true
      if (this.isSelected(p)) return false
      return this.selectedParticipantIds.length >= this.remainingSlots(this.selectedRoom)
    },
    genderLabel (g) {
      const map = { M: 'Masculino', F: 'Feminino', male: 'Masculino', female: 'Feminino' }
      return map[g] || g
    },
    groupName (p) {
      if (p.group && p.group.data && p.group.data.name) return p.group.data.name
      return p.group_display || ''
    },
    companionsOf (participantId) {
      const p = this.participants.find(x => x.id === participantId)
      if (!p || !p.companion || !p.companion.data) return []
      return p.companion.data
    },
    doAllocate (participantIds) {
      const remaining = this.remainingSlots(this.selectedRoom)
      if (participantIds.length > remaining) {
        errorMessage(`Não há vagas suficientes: o quarto tem ${remaining} vaga(s) e você tentou alocar ${participantIds.length} pessoa(s).`)
        return
      }
      const payload = {
        hotel_id: this.hotelId,
        room_id: this.selectedRoom.id,
        hotel_date_range_id: this.dateRangeId,
        participant_ids: participantIds
      }
      postGroupData(payload).then(() => {
        successMessage('Alocação realizada com sucesso.', 2000)
        this.selectedParticipantIds = []
        this.loadRooms()
        this.loadParticipants()
        this.$emit('allocated')
      }).catch((err) => {
        const msg = err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : err.message
        errorMessage('Falha: ' + msg)
      })
    },
    allocate () {
      if (!this.canAllocate) return
      const base = [...this.selectedParticipantIds]

      const companions = []
      base.forEach(id => {
        this.companionsOf(id).forEach(c => {
          if (!base.includes(c.id) && !companions.find(x => x.id === c.id)) {
            companions.push(c)
          }
        })
      })

      if (companions.length) {
        const names = companions.map(c => c.name).join(', ')
        Swal.fire({
          title: 'Alocar acompanhantes juntos?',
          html: `Os selecionados possuem <b>${companions.length}</b> acompanhante(s):<br><small>${names}</small><br><br>Deseja alocá-los no mesmo quarto?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#556ee6',
          cancelButtonText: 'Não, só os selecionados',
          confirmButtonText: 'Sim, alocar juntos'
        }).then((result) => {
          if (result.value) {
            this.doAllocate([...base, ...companions.map(c => c.id)])
          } else {
            this.doAllocate(base)
          }
        })
      } else {
        this.doAllocate(base)
      }
    },
    removeAllocation (alloc) {
      Swal.fire({
        title: 'Remover alocação?',
        text: 'O participante voltará para a lista de não alocados.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#f46a6a',
        cancelButtonText: 'Cancelar',
        confirmButtonText: 'Sim, remover'
      }).then((result) => {
        if (result.value) {
          delAllocation(alloc.participant_id).then(() => {
            successMessage('Alocação removida.', 1500)
            this.loadRooms()
            this.loadParticipants()
            this.$emit('allocated')
          }).catch(() => {
            errorMessage('Falha ao remover alocação.')
          })
        }
      })
    }
  }
}
</script>

<style scoped>
.fa-overlay {
  position: fixed;
  inset: 0;
  z-index: 1035;
  background: #f5f6fa;
  display: flex;
  flex-direction: column;
}

/* Topbar */
.fa-topbar {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  padding: 12px 18px;
  background: #fff;
  border-bottom: 1px solid #e3e8f0;
  flex-wrap: wrap;
}
.fa-topbar-title {
  font-weight: 700;
  font-size: 15px;
  color: #1b2a4a;
  white-space: nowrap;
  padding-bottom: 6px;
}
.fa-topbar-title i { color: #556ee6; font-size: 18px; vertical-align: middle; }
.fa-topbar-controls { display: flex; gap: 12px; flex: 1; flex-wrap: wrap; }
.fa-ctrl { display: flex; flex-direction: column; min-width: 170px; }
.fa-ctrl-cat { min-width: 220px; max-width: 300px; }
/* Multi-select compacto: 1 linha fixa (1 chip + "+N"), tipografia dos
   demais selects da topbar */
.fa-ctrl-cat ::v-deep .multiselect { font-size: 13px; min-height: 38px; }
.fa-ctrl-cat ::v-deep .multiselect__tags {
  min-height: 38px !important;
  max-height: 38px;
  overflow: hidden;
  white-space: nowrap;
  padding: 6px 40px 0 8px !important;
  font-size: 13px;
  border-color: #ced4da;
  border-radius: 4px;
}
.fa-ctrl-cat ::v-deep .multiselect__tags-wrap { display: inline-flex; align-items: center; max-width: 100%; }
.fa-ctrl-cat ::v-deep .multiselect__tag {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 0;
  vertical-align: middle;
}
.fa-ctrl-cat ::v-deep .multiselect__strong {
  display: inline-block;
  margin-bottom: 0;
  padding: 2px 6px;
  border-radius: 5px;
  background: #eef1fb;
  color: #556ee6;
  font-size: 12px;
  font-weight: 600;
  vertical-align: middle;
}
.fa-ctrl-cat ::v-deep .multiselect__placeholder {
  color: #495057;
  font-size: 13px;
  margin-bottom: 0;
  padding-top: 2px;
}
.fa-ctrl-cat ::v-deep .multiselect__input { font-size: 13px; }
.fa-ctrl-cat ::v-deep .multiselect__select { height: 36px; }
.fa-ctrl label { font-size: 11px; font-weight: 600; color: #7a8ba7; margin-bottom: 3px; }
.fa-close {
  border: none; background: transparent; font-size: 26px; line-height: 1;
  color: #7a8ba7; cursor: pointer; padding: 0 0 4px 0;
}
.fa-close:hover { color: #f46a6a; }

/* Body */
.fa-body { flex: 1; display: flex; overflow: hidden; min-height: 0; }
.fa-empty {
  flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
  color: #c4cfde;
}
.fa-empty i { font-size: 64px; }
.fa-muted { color: #97a0af; font-size: 13px; }

/* Rooms panel */
.fa-rooms {
  flex: 1 1 62%;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  position: relative;
}

/* Loading overlay (padrão form-builder) */
.fa-loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(245, 246, 250, 0.82);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
}
.fa-loading-content {
  text-align: center;
  background: #fff;
  padding: 24px 40px;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, .1);
}
.fa-loading-content p { margin: 14px 0 0; font-size: 14px; font-weight: 500; color: #495057; }
.fa-loading-spinner {
  width: 34px; height: 34px; margin: 0 auto;
  border: 3px solid #e9ecef; border-top-color: #556ee6; border-radius: 50%;
  animation: fa-spin .8s linear infinite;
}
@keyframes fa-spin { to { transform: rotate(360deg); } }
.fa-fade-enter-active { transition: opacity .15s ease; }
.fa-fade-leave-active { transition: opacity .3s ease; }
.fa-fade-enter, .fa-fade-leave-to { opacity: 0; }
.fa-rooms-head { padding: 14px 18px 8px; }
.fa-legend { display: flex; gap: 16px; font-size: 12px; color: #5b6b85; }
.fa-room-scroll { flex: 1; min-height: 0; overflow-y: auto; padding: 4px 18px 14px; }
.fa-room-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(92px, 1fr));
  gap: 10px;
}
.fa-room {
  position: relative;
  border: 2px solid #e3e8f0;
  border-radius: 10px;
  background: #fff;
  padding: 10px 6px;
  text-align: center;
  cursor: pointer;
  transition: transform .08s ease, box-shadow .08s ease;
}
.fa-room:hover { transform: translateY(-1px); box-shadow: 0 2px 8px rgba(27,42,74,.1); }

/* Badge no canto superior direito do card (padrão dos modais de alocação) */
.fa-room-badge {
  position: absolute;
  top: 5px;
  right: 5px;
  line-height: 1;
}
.fa-badge-check { font-size: 16px; color: #34c38f; }
.fa-badge-partial, .fa-badge-full {
  width: 16px; height: 16px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 10px; font-weight: 600;
}
.fa-badge-partial { background: #f1b44c; }
.fa-badge-full { background: #f46a6a; }
.fa-room.empty { border-color: #34c38f; }
.fa-room.partial { border-color: #f1b44c; background: #fffdf7; }
.fa-room.full { border-color: #f46a6a; background: #fff7f7; }
.fa-room.selected { box-shadow: 0 0 0 3px rgba(85,110,230,.35); border-color: #556ee6; }
.fa-room-num { font-weight: 700; font-size: 15px; color: #1b2a4a; }
.fa-room-cap { font-size: 12px; color: #5b6b85; }
.fa-room-type { font-size: 10px; color: #97a0af; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* Footer fixo do detalhe do quarto */
.fa-room-detail {
  flex-shrink: 0;
  background: #fff;
  border-top: 1px solid #e3e8f0;
  padding: 12px 18px;
  max-height: 30%;
  overflow-y: auto;
  box-shadow: 0 -2px 10px rgba(27,42,74,.05);
}
.fa-room-detail-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.fa-slots { font-size: 12px; color: #556ee6; font-weight: 600; }
.fa-occupants { display: flex; flex-wrap: wrap; gap: 6px; }
.fa-occupant {
  display: inline-flex; align-items: center; gap: 6px;
  background: #eef1fe; color: #3d4f6f; border-radius: 6px; padding: 3px 8px; font-size: 12px;
}
.fa-occupant-name { cursor: pointer; }
.fa-occupant-name:hover { color: #556ee6; text-decoration: underline; }
.fa-remove { cursor: pointer; color: #f46a6a; font-weight: 700; }

/* Participants panel */
.fa-participants {
  flex: 1 1 38%;
  min-width: 340px;
  max-width: 480px;
  background: #fff;
  border-left: 1px solid #e3e8f0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}
.fa-part-head { display: flex; flex-direction: column; gap: 8px; padding: 14px; border-bottom: 1px solid #eef1f6; }
.fa-part-head-top { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.fa-part-head-bottom { display: flex; align-items: center; gap: 8px; }
.fa-count { font-size: 13px; font-weight: 600; color: #1b2a4a; white-space: nowrap; }
.fa-count i { color: #556ee6; }

/* Botão quadrado do filtro de acompanhante (toggle do b-dropdown) */
::v-deep .fa-comp-btn {
  width: 38px;
  height: 38px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}
::v-deep .fa-comp-btn-active {
  background: #556ee6;
  border-color: #556ee6;
  color: #fff;
}
.fa-part-list { flex: 1; min-height: 0; overflow-y: auto; }
.fa-part-item {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px; border-bottom: 1px solid #f4f6f9; margin: 0; cursor: pointer;
}
.fa-part-item:hover { background: #fafbfe; }
.fa-part-item.disabled { opacity: .5; cursor: not-allowed; }
.fa-part-info { flex: 1; }
.fa-part-name { font-size: 13px; font-weight: 600; color: #1b2a4a; }
.fa-part-meta { font-size: 11px; color: #7a8ba7; display: flex; gap: 5px; flex-wrap: wrap; align-items: center; }
.fa-comp-badge { color: #556ee6; font-weight: 600; }
.fa-part-footer {
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 14px; border-top: 1px solid #eef1f6; background: #fff;
}
.fa-selected-count { font-size: 13px; color: #5b6b85; }
.fa-alloc-hint { display: flex; align-items: center; gap: 6px; margin: 0 0 8px; padding: 7px 10px; font-size: 12.5px; color: #723b13; background: #fdf6b2; border-radius: 6px; }
.fa-alloc-hint i { font-size: 15px; }
.fa-alloc-btn {
  padding: 0.55rem 1.4rem;
  font-weight: 600;
  border-radius: 8px;
  font-size: 14px;
}

/* Scrollbars finas (padrão form-builder) */
.fa-room-scroll,
.fa-part-list,
.fa-room-detail {
  scrollbar-width: thin;
  scrollbar-color: #d0d5dd transparent;
}
.fa-room-scroll::-webkit-scrollbar,
.fa-part-list::-webkit-scrollbar,
.fa-room-detail::-webkit-scrollbar { width: 6px; }
.fa-room-scroll::-webkit-scrollbar-track,
.fa-part-list::-webkit-scrollbar-track,
.fa-room-detail::-webkit-scrollbar-track { background: transparent; }
.fa-room-scroll::-webkit-scrollbar-thumb,
.fa-part-list::-webkit-scrollbar-thumb,
.fa-room-detail::-webkit-scrollbar-thumb { background-color: #d0d5dd; border-radius: 3px; }
.fa-room-scroll::-webkit-scrollbar-thumb:hover,
.fa-part-list::-webkit-scrollbar-thumb:hover,
.fa-room-detail::-webkit-scrollbar-thumb:hover { background-color: #b0b5bd; }

@media (max-width: 900px) {
  .fa-body { flex-direction: column; overflow-y: auto; }
  .fa-rooms, .fa-participants { flex: none; }
  .fa-participants { max-width: none; border-left: none; border-top: 1px solid #e3e8f0; }
}
</style>
